import {type IResponse} from '../../@type/response';

type Maybe<T> = {result?: T; error?: unknown};

/**
 * Takes a promise and returns a Maybe type of the result or an error. This can be used to
 * avoid try/catch blocks which can be difficult to read and maintain.
 */
async function maybe<TResult = IResponse<void>>(promise: Promise<TResult>): Promise<Maybe<TResult>> {
	return promise
		.then(result => ({result}))
		.catch((error: unknown) => ({error})) as Maybe<TResult>;
}

/**
 * Wraps a fetch request and returns a Maybe type of the resulting JSON or an error. This
 * can be used to avoid try/catch blocks which can be difficult to read and maintain.
 */
async function maybeFetch<TResult = IResponse<void>>(input: RequestInfo | URL, init?: RequestInit | undefined): Promise<Maybe<TResult>> {
	return fetch(input, init)
		.then(async response => response.json() as Promise<TResult>)
		.then(result => ({result}))
		.catch((error: unknown) => ({error})) as Maybe<TResult>;
}

/**
 * This is a special version of maybeFetch that is used to fetch data from WordPress. WordPress sometimes
 * has bad plugins which will result in malformed JSON responses. This function will attempt to fix the malformed
 * JSON and return the corrected result.
 *
 * @param input The request URL
 * @param init The request init object
 * @param regexSelector A regex selector that will be used to extract the JSON from the malformed response if one is encountered.
 */
async function maybeFetchWP<TResult = IResponse<void>>(input: RequestInfo | URL, init?: RequestInit | undefined, regexSelector = /{\s*".*[^{}]*}/gs): Promise<Maybe<TResult>> {
	return fetch(input, init)
		.then(async response => response.text())
		.then(jsonText => {
			try {
				return {result: JSON.parse(jsonText) as TResult} as Maybe<TResult>;
			} catch (error: unknown) {
				// Attempt to extract JSON from malformed response json text
				const extractedJSONText = regexSelector.exec(jsonText);

				if (extractedJSONText === null || !extractedJSONText[0]) {
					console.log('Unable to fix malformed JSON');
					// Could not fix malformed JSON so just return the original error.
					return {error} as Maybe<TResult>;
				}

				console.log('Fixed malformed JSON. Original:');
				console.log(jsonText);
				return {result: JSON.parse(extractedJSONText[0]) as TResult} as Maybe<TResult>;
			}
		})
		.catch((error: unknown) => ({error} as Maybe<TResult>));
}

export {
	type Maybe,

	maybe,
	maybeFetch,
	maybeFetchWP,
};
